<!-- eslint-disable vue/valid-v-slot -->
<template>
  <v-dialog v-model="isDialogVisible" persistent max-width="900px">
    <v-card>
      <v-card-title class="d-flex align-center py-2 px-4">
        <span class="subtitle-1 font-weight-medium">TR Work File Export Job</span>
        <span class="caption grey--text ml-2">(CST Timezone)</span>
        <v-spacer></v-spacer>
        <v-btn icon small @click="closeDialog">
          <v-icon>{{ icons.mdiClose }}</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="pt-4 pb-0">
        <v-row>
          <!-- LEFT: identity + export config -->
          <v-col cols="12" md="7" class="pr-md-4">
            <v-row dense>
              <v-col cols="12">
                <v-text-field
                  v-model="localItem.description"
                  v-uppercase="globalSettingItem.conEverythingToCaps"
                  label="Job Name"
                  dense
                  autofocus
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12">
                <v-autocomplete
                  v-model="localItem.params.profileName"
                  label="TR Work File Profile"
                  :items="trWorkFileProfiles"
                  dense
                  clearable
                  :loading="loadingProfiles"
                ></v-autocomplete>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12">
                <v-autocomplete
                  v-model="localItem.params.exportProfile"
                  label="Export Profile"
                  :items="trWorkFileExportProfiles"
                  item-text="name"
                  item-value="name"
                  dense
                  clearable
                  :loading="loadingExportProfiles"
                ></v-autocomplete>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12">
                <v-text-field
                  v-model="localItem.params.targetPath"
                  label="Preferred Location (Target Path)"
                  hint="Must start with /data/exports/"
                  persistent-hint
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="12">
                <v-text-field
                  v-model="localItem.outputFilename"
                  label="Output Filename"
                  hint="$TODAY_MMDDYYYY = today's date. Default: UnpaidTaxRecords_$TODAY_MMDDYYYY.csv"
                  persistent-hint
                  dense
                  clearable
                >
                  <template v-slot:append>
                    <v-btn x-small text color="primary"
                      @click="localItem.outputFilename = 'UnpaidTaxRecords_$TODAY_MMDDYYYY.csv'">
                      Reset
                    </v-btn>
                  </template>
                </v-text-field>
              </v-col>
            </v-row>
            <v-row dense class="align-center">
              <v-col cols="12" sm="6">
                <v-checkbox
                  v-model="auditEnabled"
                  label="Enable record-count audit"
                  dense
                  hide-details
                  @change="onAuditToggle"
                ></v-checkbox>
              </v-col>
              <v-col v-if="auditEnabled" cols="12" sm="6">
                <v-select
                  v-model="localItem.params.recordCountKey"
                  label="Count column"
                  :items="recordCountOptions"
                  item-text="label"
                  item-value="value"
                  dense
                  hide-details
                ></v-select>
              </v-col>
            </v-row>
            <v-row dense class="align-center">
              <v-col cols="12">
                <v-checkbox
                  v-model="uploadEnabled"
                  label="Enable Forte FTP upload"
                  dense
                  hide-details
                  @change="onUploadToggle"
                ></v-checkbox>
              </v-col>
            </v-row>
            <v-row v-if="uploadEnabled" dense class="align-center">
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="localItem.params.uploadFileName"
                  label="Upload as"
                  hint="Transfer name. Forte requires .u01 while the upload is in flight."
                  persistent-hint
                  dense
                  clearable
                ></v-text-field>
              </v-col>
              <v-col cols="12" sm="6">
                <v-text-field
                  v-model="localItem.params.uploadRenameTo"
                  label="Rename to after upload"
                  hint="Final name at Forte root. Leave blank to skip the rename."
                  persistent-hint
                  dense
                  clearable
                >
                  <template v-slot:append>
                    <v-btn x-small text color="primary" @click="resetUploadNames">
                      Reset
                    </v-btn>
                  </template>
                </v-text-field>
              </v-col>
              <v-col v-if="uploadPairWarning" cols="12">
                <div class="caption error--text">{{ uploadPairWarning }}</div>
              </v-col>
            </v-row>
          </v-col>

          <!-- RIGHT: schedule + active toggle -->
          <v-col cols="12" md="5">
            <v-sheet outlined rounded class="pa-3">
              <div class="caption grey--text mb-3">Schedule (CST)</div>
              <v-row dense>
                <v-col cols="6">
                  <v-text-field
                    v-model="localItem.expectedRunTime.days"
                    label="Days (*, 0-7, or range e.g. 1-5)"
                    :rules="[cronDaysRule]"
                    dense
                    @focus="$event.target.select()"
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="localItem.expectedRunTime.hrs"
                    label="Hrs"
                    type="number"
                    dense
                    @focus="$event.target.select()"
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="localItem.expectedRunTime.mins"
                    label="Mins"
                    type="number"
                    dense
                    @focus="$event.target.select()"
                  ></v-text-field>
                </v-col>
                <v-col cols="6">
                  <v-text-field
                    v-model="localItem.expectedRunTime.secs"
                    label="Secs"
                    type="number"
                    dense
                    @focus="$event.target.select()"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-divider class="my-2"></v-divider>
              <BinaryToggle
                class="mt-2"
                id="trWorkFileExportDialogActiveToggle"
                aria-label="Job state: inactive or active"
                off-label="Inactive"
                on-label="Active"
                v-model="localItem.isActiveInactive" />
            </v-sheet>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="px-4 pb-3 pt-2">
        <v-spacer></v-spacer>
        <v-btn color="blue darken-1" text @click="closeDialog">CLOSE</v-btn>
        <v-btn color="blue darken-1" @click="save">SAVE</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  mapActions, mapMutations, mapGetters, mapState,
} from 'vuex';
import { mdiClose } from '@mdi/js';
import BinaryToggle from '../common/base/BinaryToggle.vue';
import { amountToNumber } from '../../util/shared/vue-global';
import { normalizeCronDays, isValidCronDays } from '../../util/shared/cronDays';
import GlobalToCaps from '../../util/shared/GlobalToCaps.mixin.vue';

const TR_WORK_FILE_EXPORT_KIND = 'trWorkFileExport';
const TR_WORK_FILE_EXPORT_DESTINATION = '/tax/trWorkFile/profile-export';

// Forte names the IVR batch file btco<orgId>-p<locationId>. It must be uploaded with a
// .u01 extension and renamed to .b01 once the transfer completes -- their batch picker
// claims .b01, so the final extension must not appear until the file is whole.
const FORTE_UPLOAD_STEM = 'btco259412-p317322';
const FORTE_TEMP_NAME = `${FORTE_UPLOAD_STEM}.u01`;
const FORTE_FINAL_NAME = `${FORTE_UPLOAD_STEM}.b01`;

// Forte matches the two names on the part before the extension, so a mismatched pair
// uploads fine and then fails the rename.
const stemOf = (name) => String(name || '').replace(/\.[^.]*$/, '');

const emptyItem = () => ({
  description: '',
  destination: TR_WORK_FILE_EXPORT_DESTINATION,
  jobKind: TR_WORK_FILE_EXPORT_KIND,
  params: {
    profileName: '',
    exportProfile: 'DELQ TAX CSV',
    listName: '',
    wkstID: 0,
    targetPath: '/data/exports/f/ascii/over-night-reports/$TODAY',
    // present at init so Vue 2.6 makes it reactive when the audit select binds to it
    recordCountKey: undefined,
    // Forte FTP upload (opt-in). uploadSftpType is the on-switch (fixed 'forteUnpaids');
    // uploadFileName is the name the file is transferred under and uploadRenameTo the
    // name it is promoted to once complete. Declared here so Vue 2.6 tracks them
    // reactively when the upload fields bind.
    uploadSftpType: undefined,
    uploadFileName: undefined,
    uploadRenameTo: undefined,
  },
  outputFilename: 'UnpaidTaxRecords_$TODAY_MMDDYYYY.csv',
  expectedRunTime: {
    days: '*', hrs: 2, mins: 0, secs: 0,
  },
  isActiveInactive: false,
});

export default {
  name: 'TrWorkFileExportDialog',
  components: { BinaryToggle },
  mixins: [GlobalToCaps],
  data() {
    return {
      localItem: emptyItem(),
      loadingProfiles: false,
      loadingExportProfiles: false,
      trWorkFileProfiles: [],
      trWorkFileExportProfiles: [],
      auditEnabled: false,
      uploadEnabled: false,
      recordCountOptions: [
        { label: 'Unpaids', value: 'UnpaidsRecCt' },
        { label: 'Paids', value: 'PaidsRecCt' },
        { label: 'Paid Details', value: 'PaidsDetRecCt' },
        { label: 'Adjustments', value: 'AdjustRecCt' },
      ],
      icons: { mdiClose },
    };
  },
  props: {
    closeDialogBox: {
      type: Boolean,
      default: false,
    },
    baseCrudKey: {
      type: String,
      default: '',
    },
    rowItem: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    ...mapGetters('base/crud', ['item', 'items', 'index', 'isVisible']),
    ...mapState({
      globalSettingItem: (state) => state.SystemConfig.globalSettingItem || {},
    }),
    isDialogVisible: {
      get() { return this.closeDialogBox; },
      set(value) { this.$emit('settingDialogVal', value); },
    },
    // Non-blocking: flags the misconfiguration that would upload fine and then fail the
    // rename, which is otherwise only visible in the backend cron log.
    uploadPairWarning() {
      const p = this.localItem.params || {};
      if (!this.uploadEnabled || !p.uploadFileName || !p.uploadRenameTo) return '';
      if (stemOf(p.uploadFileName) !== stemOf(p.uploadRenameTo)) {
        return 'Both names must match before the extension, otherwise the rename will fail.';
      }
      return '';
    },
  },
  watch: {
    closeDialogBox(val) {
      if (val) {
        this.$nextTick(() => {
          const base = emptyItem();
          const saved = this.rowItem || {};
          this.localItem = {
            ...base,
            ...JSON.parse(JSON.stringify(saved)),
            destination: TR_WORK_FILE_EXPORT_DESTINATION,
            jobKind: TR_WORK_FILE_EXPORT_KIND,
            params: {
              ...base.params,
              ...(saved.params || {}),
            },
          };
          if (!this.localItem.outputFilename) {
            this.localItem.outputFilename = 'UnpaidTaxRecords_$TODAY_MMDDYYYY.csv';
          }
          this.auditEnabled = !!(this.localItem.params && this.localItem.params.recordCountKey);
          this.uploadEnabled = !!(this.localItem.params && this.localItem.params.uploadSftpType);
        });
        this.loadPickerOptions();
      } else {
        this.localItem = emptyItem();
        this.auditEnabled = false;
        this.uploadEnabled = false;
      }
    },
  },
  methods: {
    ...mapActions('cron/cronJobUtil', ['upsertCronJob', 'getTrWorkFileProfiles', 'getTrWorkFileExportProfiles']),
    ...mapMutations('base/crud', ['setCriteria', 'setItems', 'setItem', 'setIndex']),
    closeDialog() {
      this.$emit('settingDialogVal', false);
      this.$nextTick(() => { this.setIndex([this.baseCrudKey, -1]); });
    },
    onAuditToggle(val) {
      if (!val && this.localItem.params) { this.localItem.params.recordCountKey = undefined; }
    },
    onUploadToggle(val) {
      if (!this.localItem.params) { return; }
      if (val) {
        // Prefill the .u01/.b01 pair; ops can edit either name.
        if (!this.localItem.params.uploadFileName) {
          this.localItem.params.uploadFileName = FORTE_TEMP_NAME;
        }
        if (!this.localItem.params.uploadRenameTo) {
          this.localItem.params.uploadRenameTo = FORTE_FINAL_NAME;
        }
      } else {
        this.localItem.params.uploadSftpType = undefined;
        this.localItem.params.uploadFileName = undefined;
        this.localItem.params.uploadRenameTo = undefined;
      }
    },
    resetUploadNames() {
      if (!this.localItem.params) { return; }
      this.localItem.params.uploadFileName = FORTE_TEMP_NAME;
      this.localItem.params.uploadRenameTo = FORTE_FINAL_NAME;
    },
    async loadPickerOptions() {
      this.loadingProfiles = true;
      this.getTrWorkFileProfiles().then((profiles) => {
        this.trWorkFileProfiles = profiles || [];
        this.loadingProfiles = false;
      }).catch(() => { this.loadingProfiles = false; });

      this.loadingExportProfiles = true;
      this.getTrWorkFileExportProfiles().then((exportProfiles) => {
        this.trWorkFileExportProfiles = exportProfiles || [];
        this.loadingExportProfiles = false;
      }).catch(() => { this.loadingExportProfiles = false; });
    },
    cronDaysRule(v) {
      return isValidCronDays(v)
        || 'Use *, a day 0-7, an ascending range like 1-5, or a list like 1,3,5';
    },
    async save() {
      const item = JSON.parse(JSON.stringify(this.localItem));
      item.destination = TR_WORK_FILE_EXPORT_DESTINATION;
      item.jobKind = TR_WORK_FILE_EXPORT_KIND;
      const { expectedRunTime } = item;
      expectedRunTime.days = normalizeCronDays(expectedRunTime.days);
      if (!isValidCronDays(expectedRunTime.days)) return; // invalid; the field rule shows why
      expectedRunTime.hrs = amountToNumber(expectedRunTime.hrs);
      expectedRunTime.mins = amountToNumber(expectedRunTime.mins);
      expectedRunTime.secs = amountToNumber(expectedRunTime.secs);
      delete item.nextExpectedRunTime;
      // derive listName from profileName if not set
      if (!item.params.listName && item.params.profileName) {
        item.params.listName = `CRON_TRWF_${item.params.profileName.replace(/[^A-Z0-9]/gi, '_').toUpperCase()}`;
      }
      // store fileName inside params too (orchestrator uses params.fileName)
      item.params.fileName = item.outputFilename;
      // Opt-in record-count audit: drop the key entirely when disabled
      if (!this.auditEnabled || !item.params.recordCountKey) {
        delete item.params.recordCountKey;
      }
      // Opt-in Forte FTP upload: uploadSftpType is fixed ('forteUnpaids') and is the
      // on-switch the backend reads. Drop all upload params when disabled.
      if (this.uploadEnabled) {
        item.params.uploadSftpType = 'forteUnpaids';
        if (!item.params.uploadFileName) { delete item.params.uploadFileName; }
        // Absent uploadRenameTo means "upload only, no rename" on the backend.
        if (!item.params.uploadRenameTo) { delete item.params.uploadRenameTo; }
      } else {
        delete item.params.uploadSftpType;
        delete item.params.uploadFileName;
        delete item.params.uploadRenameTo;
      }
      await this.upsertCronJob({ item, baseCrudKey: this.baseCrudKey }).then(() => {
        this.closeDialog();
      });
    },
  },
};
</script>
